import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  LinearProgress,
  Tooltip,
  Fab,
  Paper,
  Divider,
  Stack,
  Autocomplete,
  InputAdornment,
  Collapse,
  Badge,
  Avatar,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid';
import {
  Add,
  MoreVert,
  QrCode,
  Edit,
  Delete,
  Visibility,
  PlayArrow,
  CheckCircle,
  Warning,
  AccessTime,
  Assignment,
  Print,
  Search,
  FilterList,
  Refresh,
  Download,
  Upload,
  Science,
  LocationOn,
  CalendarToday,
  Person,
  Business,
  TrendingUp,
  ExpandMore,
  ExpandLess,
  Clear,
  GetApp,
  Share,
  Star,
  StarBorder,
  Notifications,
  Schedule,
  Analytics,
  Home,
} from '@mui/icons-material';
import { useAuth, UserRole } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import samplesService, { 
  Sample, 
  SampleStatus, 
  SampleType,
  Priority, 
  SampleFilters,
  SampleListResponse 
} from '../../services/samplesService';

const SamplesList: React.FC = () => {
  const { user, hasRole, canAccessSample } = useAuth();
  const navigate = useNavigate();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<SampleStatus | ''>('');
  const [workflowNotes, setWorkflowNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Pagination and filtering
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalSamples, setTotalSamples] = useState(0);
  const [filters, setFilters] = useState<SampleFilters>({});
  
  // Advanced filtering states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SampleStatus[]>([]);
  const [typeFilter, setTypeFilter] = useState<SampleType[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<Priority[]>([]);
  const [clientFilter, setClientFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState<Date | null>(null);
  const [dateToFilter, setDateToFilter] = useState<Date | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'cards'>('grid');

  // Load samples from API
  useEffect(() => {
    loadSamples();
  }, [page, pageSize, filters]);

  // Update filters when filter states change
  useEffect(() => {
    const newFilters: SampleFilters = {
      search: searchTerm || undefined,
      status: statusFilter.length > 0 ? statusFilter : undefined,
      type: typeFilter.length > 0 ? typeFilter : undefined,
      priority: priorityFilter.length > 0 ? priorityFilter : undefined,
      clientId: clientFilter || undefined,
      dateFrom: dateFromFilter || undefined,
      dateTo: dateToFilter || undefined,
    };
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, [searchTerm, statusFilter, typeFilter, priorityFilter, clientFilter, dateFromFilter, dateToFilter]);

  const loadSamples = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response: SampleListResponse = await samplesService.getSamples(page, pageSize, filters);
      
      setSamples(response.samples);
      setTotalSamples(response.total);
    } catch (error) {
      console.error('Error loading samples:', error);
      setError('Erreur lors du chargement des échantillons. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadSamples();
    setSuccess('Liste des échantillons actualisée');
    setTimeout(() => setSuccess(null), 3000);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setTypeFilter([]);
    setPriorityFilter([]);
    setClientFilter('');
    setDateFromFilter(null);
    setDateToFilter(null);
  };

  const getStatusColor = (status: SampleStatus) => {
    switch (status) {
      case SampleStatus.RECEPTION:
        return 'info';
      case SampleStatus.PREPARATION_MECANIQUE:
        return 'warning';
      case SampleStatus.ANALYSE_CHIMIQUE:
        return 'secondary';
      case SampleStatus.PRET:
        return 'success';
      case SampleStatus.EN_ATTENTE:
        return 'primary';
      case SampleStatus.REJETE:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return 'error';
      case Priority.ELEVEE:
        return 'warning';
      case Priority.NORMALE:
        return 'success';
      case Priority.CONTROLE_QUALITE:
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: SampleStatus): string => {
    switch (status) {
      case SampleStatus.RECEPTION:
        return 'Réception';
      case SampleStatus.PREPARATION_MECANIQUE:
        return 'Préparation Mécanique';
      case SampleStatus.ANALYSE_CHIMIQUE:
        return 'Analyse Chimique';
      case SampleStatus.PRET:
        return 'Prêt';
      case SampleStatus.EN_ATTENTE:
        return 'En Attente';
      case SampleStatus.REJETE:
        return 'Rejeté';
      default:
        return status;
    }
  };

  const getProgressPercentage = (sample: Sample): number => {
    switch (sample.statut) {
      case SampleStatus.RECEPTION:
        return 20;
      case SampleStatus.PREPARATION_MECANIQUE:
        return 40;
      case SampleStatus.ANALYSE_CHIMIQUE:
        return 70;
      case SampleStatus.PRET:
        return 100;
      case SampleStatus.EN_ATTENTE:
        return 50;
      case SampleStatus.REJETE:
        return 0;
      default:
        return 0;
    }
  };

  const canPerformAction = (sample: Sample, action: string): boolean => {
    if (!user) return false;
    
    // Admin can do everything
    if (user.role === UserRole.ADMIN) return true;
    
    switch (action) {
      case 'workflow':
        return canAccessSample(sample.statut);
      case 'certificate':
        return sample.statut === SampleStatus.PRET && hasRole([UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER]);
      case 'edit':
        return hasRole([UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER]);
      case 'delete':
        return hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER]);
      default:
        return false;
    }
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, sample: Sample) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedSample(sample);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedSample(null);
  };

  const handleWorkflowChange = async () => {
    if (!selectedSample || !newStatus) return;

    setLoading(true);
    try {
      await samplesService.updateSampleStatus(selectedSample.id, newStatus as SampleStatus, workflowNotes);
      
      setSuccess(`Statut de l'échantillon ${selectedSample.sample_id} mis à jour avec succès`);
      setTimeout(() => setSuccess(null), 5000);
      
      // Reload samples to reflect changes
      await loadSamples();
      
      setWorkflowDialogOpen(false);
      setNewStatus('');
      setWorkflowNotes('');
      setSelectedSample(null);
    } catch (error) {
      console.error('Error updating sample status:', error);
      setError('Erreur lors de la mise à jour du statut. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedSamples.length === 0) return;
    

    setSuccess(`Action ${action} appliquée à ${selectedSamples.length} échantillons`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const columns: GridColDef[] = [
    {
      field: 'sample_id',
      headerName: 'Code Échantillon',
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
            {params.value.slice(-2)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.external_id || 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'client',
      headerName: 'Client',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {params.value.company_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.value.contact_person || 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'type_echantillon',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          variant="outlined"
          sx={{ fontWeight: 'medium' }}
        />
      ),
    },
    {
      field: 'nom_projet',
      headerName: 'Projet / Site',
      width: 220,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {params.value || 'Aucun projet'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 12, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.site_minier || 'Site non spécifié'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'statut',
      headerName: 'Statut',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={getStatusLabel(params.value)} 
            color={getStatusColor(params.value)}
            size="small"
            sx={{ fontWeight: 'medium' }}
          />
          <LinearProgress 
            variant="determinate" 
            value={getProgressPercentage(params.row)} 
            sx={{ width: 40, height: 4, borderRadius: 2 }}
            color={getStatusColor(params.value) === 'default' ? 'primary' : getStatusColor(params.value) as any}
          />
        </Box>
      ),
    },
    {
      field: 'priorite',
      headerName: 'Priorité',
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={getPriorityColor(params.value)}
          size="small"
          variant={params.value === Priority.URGENTE ? 'filled' : 'outlined'}
          sx={{ fontWeight: 'medium' }}
        />
      ),
    },
    {
      field: 'localisation_stockage',
      headerName: 'Localisation',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value || 'Non définie'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'date_reception',
      headerName: 'Date Réception',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value ? new Date(params.value).toLocaleDateString('fr-FR') : 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        const sample = params.row as Sample;
        const actions = [];

        actions.push(
          <GridActionsCellItem
            icon={<QrCode />}
            label="QR Code"
                            onClick={() => {}}
            showInMenu
          />
        );

        if (canPerformAction(sample, 'workflow')) {
          actions.push(
            <GridActionsCellItem
              icon={<PlayArrow />}
              label="Changer Statut"
              onClick={() => {
                setSelectedSample(sample);
                setWorkflowDialogOpen(true);
              }}
              showInMenu
            />
          );
        }

        actions.push(
          <GridActionsCellItem
            icon={<Visibility />}
            label="Voir Détails"
            onClick={() => navigate(`/samples/${sample.id}`)}
            showInMenu
          />
        );

        if (canPerformAction(sample, 'certificate')) {
          actions.push(
            <GridActionsCellItem
              icon={<Print />}
              label="Certificat"
              onClick={() => {}}
              showInMenu
            />
          );
        }

        if (canPerformAction(sample, 'edit')) {
          actions.push(
            <GridActionsCellItem
              icon={<Edit />}
              label="Modifier"
              onClick={() => {}}
              showInMenu
            />
          );
        }

        return actions;
      },
    },
  ];

  const statisticsCards = [
    {
      title: 'Total Échantillons',
      value: totalSamples,
      icon: <Assignment />,
      color: 'primary.main',
      change: '+12 cette semaine',
      changeColor: 'success.main'
    },
    {
      title: 'En Cours',
      value: samples.filter(s => ![SampleStatus.PRET, SampleStatus.REJETE].includes(s.statut)).length,
      icon: <AccessTime />,
      color: 'warning.main',
      change: '-3 depuis hier',
      changeColor: 'success.main'
    },
    {
      title: 'Terminés',
      value: samples.filter(s => s.statut === SampleStatus.PRET).length,
      icon: <CheckCircle />,
      color: 'success.main',
      change: '+8 aujourd\'hui',
      changeColor: 'success.main'
    },
    {
      title: 'Urgents',
      value: samples.filter(s => s.priorite === Priority.URGENTE).length,
      icon: <Warning />,
      color: 'error.main',
      change: '2 en retard',
      changeColor: 'error.main'
    },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/dashboard" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Home fontSize="small" />
          Tableau de bord
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Assignment fontSize="small" />
          Échantillons
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Gestion des Échantillons
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gérez et suivez tous vos échantillons de laboratoire
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Actualiser
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => {}}
          >
            Exporter
          </Button>
          {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER]) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/reception')}
            >
              Nouvel Échantillon
            </Button>
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statisticsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: stat.changeColor }}>
                      {stat.change}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: stat.color, width: 56, height: 56 }}>
                    {React.cloneElement(stat.icon, { sx: { fontSize: 28 } })}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Advanced Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList />
              Filtres et Recherche
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                endIcon={showAdvancedFilters ? <ExpandLess /> : <ExpandMore />}
              >
                Filtres Avancés
              </Button>
              <Button
                size="small"
                startIcon={<Clear />}
                onClick={clearAllFilters}
                disabled={!searchTerm && statusFilter.length === 0 && typeFilter.length === 0}
              >
                Effacer
              </Button>
            </Box>
          </Box>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Rechercher par code échantillon, client, projet..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Advanced Filters */}
          <Collapse in={showAdvancedFilters}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    multiple
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as SampleStatus[])}
                    label="Statut"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={getStatusLabel(value)} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.values(SampleStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {getStatusLabel(status)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    multiple
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as SampleType[])}
                    label="Type"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.values(SampleType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priorité</InputLabel>
                  <Select
                    multiple
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value as Priority[])}
                    label="Priorité"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {Object.values(Priority).map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client"
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  placeholder="Nom du client"
                />
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedSamples.length > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">
                {selectedSamples.length} échantillon(s) sélectionné(s)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" color="inherit" onClick={() => handleBulkAction('export')}>
                  Exporter
                </Button>
                <Button size="small" color="inherit" onClick={() => handleBulkAction('assign')}>
                  Assigner
                </Button>
                <Button size="small" color="inherit" onClick={() => handleBulkAction('status')}>
                  Changer Statut
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Data Grid */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataGrid
            rows={samples}
            columns={columns}
            paginationMode="server"
            rowCount={totalSamples}
            paginationModel={{ page: page - 1, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page + 1);
              setPageSize(model.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection={hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER])}
            disableRowSelectionOnClick
            loading={loading}
            sx={{ 
              height: 600,
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'grey.50',
                borderBottom: '2px solid rgba(224, 224, 224, 1)',
              },
            }}
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            onRowSelectionModelChange={(newSelection) => {
              setSelectedSamples(newSelection as string[]);
            }}
          />
        </CardContent>
      </Card>

      {/* Workflow Dialog */}
      <Dialog open={workflowDialogOpen} onClose={() => setWorkflowDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayArrow color="primary" />
            Changer le Statut - {selectedSample?.sample_id}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Statut actuel: <Chip label={selectedSample ? getStatusLabel(selectedSample.statut) : ''} size="small" />
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Nouveau Statut</InputLabel>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as SampleStatus)}
                label="Nouveau Statut"
              >
                {Object.values(SampleStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={getStatusLabel(status)} size="small" color={getStatusColor(status)} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes et commentaires"
              value={workflowNotes}
              onChange={(e) => setWorkflowNotes(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="Ajoutez des notes sur ce changement de statut..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWorkflowDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleWorkflowChange} 
            variant="contained"
            disabled={!newStatus || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {loading ? 'Mise à jour...' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Sample FAB */}
      {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER]) && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ 
            position: 'fixed', 
            bottom: 24, 
            right: 24,
            boxShadow: 3,
          }}
          onClick={() => navigate('/reception')}
        >
          <Add />
        </Fab>
      )}
    </Box>
  );
};

export default SamplesList; 